import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { AdminSidebar, AdminView, ASSIGNABLE_ADMIN_MODULES } from './AdminSidebar';
import { AdminDashboard } from './AdminDashboard';
import { AdminUserManagement } from './AdminUserManagement';
import { AdminAppSettings } from './AdminAppSettings';
import { AdminCMSPages } from './admin/AdminCMSPages';
import { AdminHomeSelector } from './admin/AdminHomeSelector';
import { AdminSEOCrud } from './admin/AdminSEOCrud';
import { AdminLinkPreview } from './admin/AdminLinkPreview';
import { AdminWebsiteServiceRequests } from './admin/AdminWebsiteServiceRequests';
import { AdminContactMessages } from './admin/AdminContactMessages';
import { AdminFAQCrud } from './admin/AdminFAQCrud';
import { AdminBlogCrud } from './admin/AdminBlogCrud';
import { AdminBusinessContext } from './admin/AdminBusinessContext';
import { AdminCustomers } from './admin/AdminCustomers';
import { AdminNavigation } from './admin/AdminNavigation';
import { AdminHeaderConfig } from './admin/AdminHeaderConfig';
import { AdminFooterConfig } from './admin/AdminFooterConfig';
import { AdminLLMManagement } from './admin/AdminLLMManagement';
import { AdminGTMSettings } from './admin/AdminGTMSettings';
import { AdminNewsletterSubscriptions } from './admin/AdminNewsletterSubscriptions';
import { AdminReadinessScanRequests } from './admin/AdminReadinessScanRequests';
import { AdminContentHelpModal } from './admin/AdminContentHelpModal';
import { ProfileModal } from './ProfileModal';
import { User as FirebaseUser } from 'firebase/auth';

interface UserProfile {
  displayName: string;
  photoUrl?: string | null;
}

interface AdminPanelProps {
  currentUser: FirebaseUser;
  currentUserRole: 'root' | 'admin' | 'moderator';
  /** Module-specific access list for non-root admins. Root always sees everything. */
  allowedModules?: AdminView[] | null;
  onBackToHome: () => void;
  userProfile: UserProfile | null;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
  onSaveProfile?: (displayName: string) => Promise<void>;
  onGoogleSignIn?: () => Promise<void>;
  onEmailSignIn?: (email: string, password: string) => Promise<void>;
  onEmailRegister?: (email: string, password: string, displayName: string) => Promise<void>;
  onPasswordReset?: (email: string) => Promise<void>;
  onSignOut?: () => Promise<void>;
  allowAnonymousSignIn?: boolean;
}

export const AdminPanel = ({
  currentUser,
  currentUserRole,
  allowedModules = null,
  onBackToHome,
  userProfile,
  showNotify,
  confirmAction,
  onSaveProfile,
  onGoogleSignIn,
  onEmailSignIn,
  onEmailRegister,
  onPasswordReset,
  onSignOut,
  allowAnonymousSignIn = true,
}: AdminPanelProps) => {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isContentHelpOpen, setIsContentHelpOpen] = useState(false);

  // URL segment <-> AdminView mapping so /admin/website-requests etc. stay in sync
  const VIEW_TO_PATH: Record<AdminView, string> = useMemo(
    () => ({
      dashboard: 'dashboard',
      pages: 'pages',
      home: 'home',
      seo: 'seo',
      linkPreview: 'link-preview',
      faq: 'faq',
      blog: 'blog',
      businessContext: 'business-context',
      websiteRequests: 'website-requests',
      contactMessages: 'contact-messages',
      newsletter: 'newsletter-subscribers',
      scanRequests: 'scan-requests',
      customers: 'customers',
      users: 'users',
      settings: 'settings',
      gtm: 'google-tag-manager',
      llmManagement: 'llm-management',
      navigation: 'navigation',
      headerConfig: 'header-config',
      footerConfig: 'footer-config',
    }),
    []
  );
  const PATH_TO_VIEW: Record<string, AdminView> = useMemo(
    () =>
      (Object.entries(VIEW_TO_PATH) as [AdminView, string][]).reduce(
        (acc, [view, path]) => {
          acc[path] = view;
          return acc;
        },
        {} as Record<string, AdminView>
      ),
    [VIEW_TO_PATH]
  );
  // For module-restricted admins, default to their first allowed module
  const defaultView: AdminView = (() => {
    if (currentUserRole === 'root' || !allowedModules || allowedModules.length === 0) return 'pages';
    return allowedModules[0];
  })();

  const currentView: AdminView =
    section && PATH_TO_VIEW[section] ? PATH_TO_VIEW[section] : defaultView;

  /** Content views where CMS embed codes can be used (page body, nav HTML, header/footer, etc.). */
  const CONTENT_VIEWS: AdminView[] = [
    'pages',
    'home',
    'navigation',
    'headerConfig',
    'footerConfig',
    'seo',
    'faq',
    'blog',
  ];
  const showEmbedCodesGuide = CONTENT_VIEWS.includes(currentView);

  // Check if the current user has access to the requested view
  const hasAccessToView = (view: AdminView): boolean => {
    if (currentUserRole === 'root') return true;
    if (!allowedModules || allowedModules.length === 0) return view !== 'users';
    return allowedModules.includes(view);
  };

  // Security check: Ensure user has admin role
  if (!currentUser || !currentUserRole || (currentUserRole !== 'root' && currentUserRole !== 'admin' && currentUserRole !== 'moderator')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">You do not have permission to access the admin panel.</p>
          <button
            onClick={onBackToHome}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const userName = userProfile?.displayName || currentUser.displayName || 'Admin';
  const userPhoto = currentUser.photoURL || userProfile?.photoUrl || null;

  const handleViewChange = (view: AdminView) => {
    const path = VIEW_TO_PATH[view];
    navigate(path ? `/admin/${path}` : '/admin', { replace: true });
  };

  const renderCurrentView = () => {
    if (!hasAccessToView(currentView)) {
      const firstAllowed = allowedModules && allowedModules.length > 0
        ? allowedModules[0]
        : ASSIGNABLE_ADMIN_MODULES[0]?.id ?? 'pages';
      return (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <p className="text-gray-500 mb-4">You do not have access to this section.</p>
          <button
            onClick={() => handleViewChange(firstAllowed)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            Go to {firstAllowed}
          </button>
        </div>
      );
    }

    const contentTitleAction = showEmbedCodesGuide ? (
      <button
        type="button"
        onClick={() => setIsContentHelpOpen(true)}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        aria-label="Open content help and embed codes guide"
      >
        <HelpCircle size={18} className="shrink-0" />
        Help &amp; guides
      </button>
    ) : undefined;

    switch (currentView) {
      case 'dashboard':
        return (
          <AdminDashboard
            currentUserId={currentUser.uid}
            currentUserRole={currentUserRole}
            allowedModules={allowedModules}
            showNotify={showNotify}
            confirmAction={confirmAction}
          />
        );
      case 'pages':
        return (
          <AdminCMSPages
            currentUserId={currentUser.uid}
            showNotify={showNotify}
            confirmAction={confirmAction}
            titleAction={contentTitleAction}
          />
        );
      case 'home':
        return (
          <AdminHomeSelector
            currentUserId={currentUser.uid}
            showNotify={showNotify}
            titleAction={contentTitleAction}
          />
        );
      case 'seo':
        return (
          <AdminSEOCrud
            showNotify={showNotify}
            confirmAction={confirmAction}
            titleAction={contentTitleAction}
          />
        );
      case 'linkPreview':
        return (
          <AdminLinkPreview
            currentUserId={currentUser.uid}
            showNotify={showNotify}
            titleAction={contentTitleAction}
          />
        );
      case 'faq':
        return (
          <AdminFAQCrud
            currentUserId={currentUser.uid}
            showNotify={showNotify}
            confirmAction={confirmAction}
            titleAction={contentTitleAction}
          />
        );
      case 'blog':
        return (
          <AdminBlogCrud
            currentUserId={currentUser.uid}
            currentUserName={userName}
            currentUserEmail={currentUser.email ?? ''}
            showNotify={showNotify}
            confirmAction={confirmAction}
            titleAction={contentTitleAction}
          />
        );
      case 'businessContext':
        return (
          <AdminBusinessContext
            currentUserId={currentUser.uid}
            showNotify={showNotify}
            confirmAction={confirmAction}
            titleAction={contentTitleAction}
          />
        );
      case 'websiteRequests':
        return <AdminWebsiteServiceRequests showNotify={showNotify} />;
      case 'contactMessages':
        return <AdminContactMessages showNotify={showNotify} />;
      case 'scanRequests':
        return <AdminReadinessScanRequests showNotify={showNotify} />;
      case 'newsletter':
        return (
          <AdminNewsletterSubscriptions
            showNotify={showNotify}
            confirmAction={confirmAction}
          />
        );
      case 'customers':
        return (
          <AdminCustomers
            showNotify={showNotify}
            confirmAction={confirmAction}
          />
        );
      case 'users':
        if (currentUserRole === 'root') {
          return (
            <AdminUserManagement
              currentUserId={currentUser.uid}
              currentUserRole={currentUserRole}
            />
          );
        }
        return null;
      case 'settings':
        return (
          <AdminAppSettings
            currentUserId={currentUser.uid}
            currentUserRole={currentUserRole}
            showNotify={showNotify}
            confirmAction={confirmAction}
          />
        );
      case 'gtm':
        return (
          <AdminGTMSettings
            currentUserId={currentUser.uid}
            showNotify={showNotify}
          />
        );
      case 'llmManagement':
        return (
          <AdminLLMManagement
            currentUserId={currentUser.uid}
            showNotify={showNotify}
          />
        );
      case 'navigation':
        return (
          <AdminNavigation
            currentUserId={currentUser.uid}
            showNotify={showNotify}
            confirmAction={confirmAction}
            titleAction={contentTitleAction}
          />
        );
      case 'headerConfig':
        return (
          <AdminHeaderConfig
            currentUserId={currentUser.uid}
            showNotify={showNotify}
            titleAction={contentTitleAction}
          />
        );
      case 'footerConfig':
        return (
          <AdminFooterConfig
            currentUserId={currentUser.uid}
            showNotify={showNotify}
            titleAction={contentTitleAction}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar Navigation - Fixed */}
      <AdminSidebar
        currentView={currentView}
        onViewChange={handleViewChange}
        onBackToHome={onBackToHome}
        userRole={currentUserRole}
        allowedModules={allowedModules}
        userName={userName}
        userPhoto={userPhoto}
        onOpenProfile={() => setIsProfileOpen(true)}
      />

      {/* Main Content Area - Offset for fixed sidebar */}
      <main className="lg:ml-56 min-w-0">
        {/* Mobile Header Spacer */}
        <div className="h-16 lg:hidden" />

        {/* Content */}
        <div className="p-4 lg:p-6">
          <div className="max-w-7xl mx-auto">
            {renderCurrentView()}
          </div>
        </div>
      </main>

      {/* Content help & guides modal (embed codes, etc.) */}
      {showEmbedCodesGuide && (
        <AdminContentHelpModal
          isOpen={isContentHelpOpen}
          onClose={() => setIsContentHelpOpen(false)}
        />
      )}

      {/* Profile Modal */}
      {onSaveProfile && onGoogleSignIn && onSignOut && (
        <ProfileModal
          isOpen={isProfileOpen}
          onClose={() => setIsProfileOpen(false)}
          user={currentUser}
          userProfile={userProfile}
          onSaveProfile={onSaveProfile}
          onGoogleSignIn={onGoogleSignIn}
          onEmailSignIn={onEmailSignIn}
          onEmailRegister={onEmailRegister}
          onPasswordReset={onPasswordReset}
          onSignOut={onSignOut}
          isAdmin={true}
          adminRole={currentUserRole}
          showNotify={showNotify || (() => {})}
          allowAnonymousSignIn={allowAnonymousSignIn}
        />
      )}
    </div>
  );
};
